import { FC } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
  formatTrackingNumber,
  getFedExTrackingUrl,
} from 'order/wizard/orderTracking/helpers/trackingHelpers';
import { ButtonIcon } from 'shared/components/Button';
import { PXSmall } from 'shared/components/Typography';
import { Wrapper } from 'shared/components/Wrapper';
import { kashmirBlue, nevada, silverChalice } from 'shared/config/Colors';

import { ReactComponent as CopyIcon } from 'assets/icons/duplicate.svg';

const TrackingNumberLink = styled.a<{ highlighted?: boolean }>`
  background-color: ${({ theme, highlighted }) =>
    highlighted ? theme.catskillWhite : 'transparent'};
  border-radius: 4px;
  color: ${({ theme }) => theme.kashmirBlue};
  font-size: 14px;
  font-weight: 600;
  margin-left: -4px;
  padding: 1px 4px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const CopyButton = styled(ButtonIcon)`
  svg {
    height: 12px;
    width: 10px;
  }
`;

interface TrackingNumberProps {
  trackingNumber: string;
  boxLabel?: string;
  highlighted?: boolean;
}

export const TrackingNumber: FC<TrackingNumberProps> = ({
  trackingNumber,
  boxLabel,
  highlighted,
}) => {
  const onCopy = () =>
    navigator.clipboard
      .writeText(trackingNumber)
      .then(() => toast.success('Tracking number copied.'))
      .catch(() => toast.error("Tracking number couldn't be copied."));

  return (
    <Wrapper inlineFlex middle gap={6}>
      <TrackingNumberLink
        href={getFedExTrackingUrl(trackingNumber)}
        target="_blank"
        rel="noopener noreferrer"
        highlighted={highlighted}
        aria-current={highlighted || undefined}
      >
        {formatTrackingNumber(trackingNumber)}
      </TrackingNumberLink>

      {boxLabel && <PXSmall color={nevada}>{boxLabel}</PXSmall>}

      <CopyButton
        type="button"
        title="Copy tracking number"
        aria-label="Copy tracking number"
        iconColor={silverChalice}
        hoverIconColor={kashmirBlue}
        onClick={onCopy}
      >
        <CopyIcon />
      </CopyButton>
    </Wrapper>
  );
};
